/* eslint camelcase: ["error", {allow: ["template_id"]}] */
import React, { FC, useEffect, useMemo, useState } from 'react';
import { map } from 'lodash/fp';
import moment from 'moment';
import { Deal, DealId } from '@upstart/harmony/lib/legos/dealTypes';
import {
  Dealer,
  DealerWithFlags,
} from '@upstart/harmony/lib/legos/dealerTypes';
import { orderBy } from 'lodash';
import { bindActionCreators, Dispatch } from 'redux';
import { connect } from 'react-redux';
import { RouteComponentProps, withRouter } from 'react-router-dom';
import { amplitudeEvents } from 'util/amplitudeEvents';
import { sendData } from 'util/amplitude';
import { useGrowthBook } from 'hooks/GrowthBook';
import { getPdfPreviewParams } from 'util/tsutils';

import {
  readFormsRequest,
  loadDealFormsRequest,
} from '../../redux/forms/actions';
import DealService from '../../services/DealService';
import BackendService from '../../services/BackendService';
import PdfEditorModal from '../../components/PencilEditor/PdfEditorModal';
import styles from './FormsAndAttachedFiles.module.scss';

interface MatchParams {
  dealId: DealId;
}

interface Props extends RouteComponentProps<MatchParams> {
  actions: {
    loadDealFormsRequest: (dealId: DealId) => void;
    readFormsRequest: (dealId: DealId) => void;
  };
  deal?: Deal;
  dealer?: Dealer;
  formTypes?: any;
  printedFormTypes?: any;
  isStandaloneViewer: boolean;
}

const isUpperCase = (character: string) => /^[^a-z]*$/.test(character);

// Used to display keys in a more user friendly format
const keyFormatter = (key: keyof Deal['attachedFiles']) => {
  if (key === 'dealSummary') {
    return 'Deal Finalized Agreement';
  }
  let formattedKey = '';
  (key as string).split('').forEach((character: string) => {
    if (isUpperCase(character)) {
      formattedKey += ' ';
    }
    formattedKey += character;
  });
  // Driver's license does not translate well to the keys
  formattedKey = formattedKey.replace('driver License', "Driver's License");
  return formattedKey;
};

const renderSorter = (sorter: () => void) => (
  <button className="pencil-order" onClick={sorter}>
    <span className="dropup">
      <span className="caret" style={{ color: 'rgb(204, 204, 204)' }} />
    </span>
    <span className="dropdown">
      <span
        className="caret"
        style={{
          color: 'rgb(204, 204, 204)',
        }}
      />
    </span>
  </button>
);

const FormsAndAttachedFiles: FC<Props> = ({
  dealer,
  deal,
  actions,
  formTypes,
  printedFormTypes,
  match,
  isStandaloneViewer,
}) => {
  const { dealId } = match.params;
  const [printedFormsMap, setPrintedFormsMap] = useState<Record<string, any>>(
    {},
  );
  const [openPDFUrl, setOpenPDFUrl] = useState('');
  const [creditApplicationPdfError, setCreditApplicationPdfError] = useState(
    false,
  );
  const [
    creditApplicationPdfLoading,
    setCreditApplicationPdfLoading,
  ] = useState(false);
  const [formTemplateId, setFormTemplateId] = useState('');
  const [formCompleted, setFormCompleted] = useState(false);
  const [sortNameAscending, setSortNameAscending] = useState(true);
  const [sortDateAscending, setSortDateAscending] = useState(false);

  const clearOpenPDFUrl = (openPDFLUrlToBeCleared: string) => {
    window.URL.revokeObjectURL(openPDFLUrlToBeCleared); // releases object from memory
    setOpenPDFUrl('');
  };

  useEffect(() => {
    actions.loadDealFormsRequest(dealId);
  }, []);

  useEffect(() => {
    sendData(amplitudeEvents.DESKING_PENCIL_FORMS_START);
  }, []);

  const formNameMap: Record<string, any> = useMemo(() => {
    if (formTypes) {
      const tempFormNameMap: Record<string, any> = {};
      // formTypes is used to get the names of the forms based on template ID.
      formTypes.forEach((formData: any) => {
        const id = formData.id as string;
        // Filter for repeating forms from legacy data
        if (
          !tempFormNameMap[id] ||
          tempFormNameMap[id].updatedAt < formData.updatedAt
        ) {
          tempFormNameMap[id] = {
            ...formData,
            template_id: id,
            completed: false,
          };
        }
      });
      return tempFormNameMap;
    }
    return {};
  }, [formTypes]);

  useEffect(() => {
    // Used to get the data for forms that have been completed. This changes the url as well to a link to the completed form
    if (Object.keys(formNameMap).length && printedFormTypes) {
      // Used so that we can get empty forms
      const tempPrintedFormsMap = { ...formNameMap };
      printedFormTypes.forEach((formData: any) => {
        const template_id = formData.template_id as string;
        const currentFormDataFromHash = tempPrintedFormsMap[template_id];
        if (
          !currentFormDataFromHash ||
          !currentFormDataFromHash.completed ||
          currentFormDataFromHash.updatedAt < formData.updatedAt
        ) {
          // Get name of the forms through the templateForms gotten from formNameMap gotten earlier
          tempPrintedFormsMap[template_id] = {
            ...formData,
            name: formNameMap[template_id]?.name,
            completed: true,
          };
        }
      });
      setPrintedFormsMap(tempPrintedFormsMap);
    }
  }, [printedFormTypes, formNameMap]);

  const growthBook = useGrowthBook();

  let darkBackground = true;
  let filesDarkBackground = true;
  const attachedFilesLength = Object.keys(deal?.attachedFiles || []).length;
  const hasPdfEditorEnabled = !!(dealer as DealerWithFlags)?.flags
    ?.useNewPdfEditorForForms;

  let creditApplicationDownloadText = 'Download';
  if (creditApplicationPdfError) {
    creditApplicationDownloadText = 'Failed to load PDF. Click to try again.';
  } else if (creditApplicationPdfLoading) {
    creditApplicationDownloadText = 'Loading...';
  }

  const handleViewFormsClick = async ({
    templateId,
    url,
    completed,
  }: {
    templateId: string;
    url: string;
    completed: boolean;
  }) => {
    setFormTemplateId(templateId);
    setFormCompleted(completed);
    sendData(amplitudeEvents.DESKING_PENCIL_FORMS_VIEW_START);
    if (completed) {
      setOpenPDFUrl(url);
      return;
    }

    const response = await BackendService.get(
      `/formTemplates/${templateId}/preview/${dealId}`,
      { params: getPdfPreviewParams(growthBook) },
    );
    const PDFLink = window.URL.createObjectURL(response);

    setOpenPDFUrl(PDFLink);
  };

  const openCreditApplicationPdf = async () => {
    if (creditApplicationPdfLoading) return;

    try {
      setCreditApplicationPdfLoading(true);
      setCreditApplicationPdfError(false);
      clearOpenPDFUrl(openPDFUrl);
      const result = await DealService.printCreditApplication(deal?.id);
      setCreditApplicationPdfLoading(false);
      setOpenPDFUrl(result);
    } catch (error) {
      setCreditApplicationPdfLoading(false);
      setCreditApplicationPdfError(true);
    }
  };

  const sortByName = () => {
    const newPrintedFormsMap: Record<string, any> = {};
    map((formData: any) => {
      const templateId = formData.template_id as string;
      if (
        !newPrintedFormsMap[templateId] ||
        newPrintedFormsMap[templateId].updatedAt < formData.updatedAt
      ) {
        newPrintedFormsMap[templateId] = formData;
      }
    }, orderBy(printedFormsMap, 'name', [sortNameAscending ? 'asc' : 'desc']));
    setPrintedFormsMap(newPrintedFormsMap);
    setSortNameAscending(!sortNameAscending);
  };

  const sortByDate = () => {
    const newPrintedFormsMap: Record<string, any> = {};
    map((formData: any) => {
      const templateId = formData.template_id as string;
      if (
        !newPrintedFormsMap[templateId] ||
        newPrintedFormsMap[templateId].updatedAt < formData.updatedAt
      ) {
        newPrintedFormsMap[templateId] = formData;
      }
    }, orderBy(printedFormsMap, ['updatedAt', 'createdAt'], [sortDateAscending ? 'asc' : 'desc', sortDateAscending ? 'asc' : 'desc']));
    setPrintedFormsMap(newPrintedFormsMap);
    setSortDateAscending(!sortDateAscending);
  };

  const renderContent = () => (
    <>
      {hasPdfEditorEnabled && formCompleted ? (
        <PdfEditorModal
          url={openPDFUrl}
          templateId={formTemplateId}
          completed={formCompleted}
          dealId={dealId}
          onPdfSaved={() => actions.readFormsRequest(dealId)}
          onClose={() => {
            sendData(amplitudeEvents.DESKING_PENCIL_FORMS_VIEW_FINISH);
            clearOpenPDFUrl(openPDFUrl);
            setFormTemplateId('');
          }}
        />
      ) : (
        <PdfEditorModal
          url={openPDFUrl}
          onClose={() => {
            sendData(amplitudeEvents.DESKING_PENCIL_FORMS_VIEW_FINISH);
            clearOpenPDFUrl(openPDFUrl);
          }}
        />
      )}

      <div className={styles.contentContainer}>
        <h4 className={styles.sectionTitle}>Forms</h4>
        <div className="pencil-editor-forms">
          <div className="pencil-editor-forms-row pencil-editor-forms-titles">
            <div className="pencil-editor-forms-title">
              <span>Title</span>
              {renderSorter(sortByName)}
            </div>
            <div className="pencil-editor-forms-title">
              <span>Date Filled</span>
              {renderSorter(sortByDate)}
            </div>
          </div>
          {Object.values(printedFormsMap).map((value: any) => {
            const { updatedAt, template_id, name, url, completed } = value;
            darkBackground = !darkBackground;
            // If the form is completed, display the completed form. If not, show a form populated with data pulled from the schema
            return (
              <div
                className={`pencil-editor-forms-row ${darkBackground &&
                  'pencil-editor-forms-row-dark'}`}
                key={name + url + template_id}
              >
                <div>{formNameMap[template_id]?.name}</div>
                <div>{!!completed && moment(updatedAt).format('LLL')}</div>
                <button
                  className="pencil-editor-forms-view-button"
                  onClick={e => {
                    e.preventDefault();
                    sendData(amplitudeEvents.DESKING_PENCIL_FORMS_VIEW_CLICK);
                    handleViewFormsClick({
                      templateId: template_id,
                      url,
                      completed,
                    });
                  }}
                >
                  View
                </button>
              </div>
            );
          })}
        </div>

        {/* For attached files like Driver's license and other scanned documents. They are images so function differently  */}
        {attachedFilesLength > 0 && (
          <>
            <h4 className={styles.sectionTitle}>Attached Files</h4>
            <div className="pencil-editor-forms">
              <div className="pencil-editor-attached-files-row pencil-editor-forms-titles">
                <div className="pencil-editor-forms-title">
                  <span>Title</span>
                </div>
              </div>
              {deal?.attachedFiles &&
                Object.keys(deal.attachedFiles).map((key: string) => {
                  if (key) {
                    const value = (deal.attachedFiles?.[
                      key as keyof Deal['attachedFiles']
                    ] as unknown) as string;
                    // Not to be included
                    if (key === 'dealSummarySignature') {
                      return null;
                    }
                    // Documents functions differently
                    if (key === 'documents') {
                      return ((value as unknown) as any[]).map(
                        ({
                          displayName,
                          imageSrc,
                        }: {
                          displayName: string;
                          imageSrc: string;
                        }) => {
                          filesDarkBackground = !filesDarkBackground;
                          return (
                            <div
                              className={`pencil-editor-attached-files-row ${filesDarkBackground &&
                                'pencil-editor-forms-row-dark'}`}
                              key={displayName}
                            >
                              <div>{displayName}</div>
                              <div>
                                <a
                                  className="pencil-editor-forms-link"
                                  target="_blank"
                                  rel="noreferrer noopener"
                                  href={imageSrc}
                                >
                                  Download
                                </a>
                              </div>
                            </div>
                          );
                        },
                      );
                    }
                    filesDarkBackground = !filesDarkBackground;
                    return (
                      <div
                        className={`pencil-editor-attached-files-row ${filesDarkBackground &&
                          'pencil-editor-forms-row-dark'}`}
                        key={key as string}
                      >
                        <div>
                          {keyFormatter(key as keyof Deal['attachedFiles'])}
                        </div>
                        {key === 'creditApplication' &&
                        !(value && new URL(value).pathname.endsWith('.pdf')) ? (
                          <div>
                            <a
                              className="pencil-editor-forms-link"
                              onClick={() => openCreditApplicationPdf()}
                            >
                              {creditApplicationDownloadText}
                            </a>
                          </div>
                        ) : (
                          <div>
                            <a
                              className="pencil-editor-forms-link"
                              target="_blank"
                              rel="noreferrer noopener"
                              href={value}
                            >
                              Download
                            </a>
                          </div>
                        )}
                      </div>
                    );
                  }
                  return null;
                })}
            </div>
          </>
        )}
      </div>
    </>
  );

  // When using inside Command Center iFrame, add additional container to provide scrolling
  if (isStandaloneViewer) {
    return <div className="pencil-editor-column">{renderContent()}</div>;
  }
  return renderContent();
};

interface Deals {
  deal: Deal;
  dealer: Dealer;
}
const mapStateToProps = ({ deals, forms }: { deals?: Deals; forms: any }) => ({
  deal: deals?.deal,
  dealer: deals?.dealer,
  formTypes: forms?.forms,
  printedFormTypes: forms?.printed_forms,
});

const mapDispatchToProps = (dispatch: Dispatch<{}>) => ({
  actions: bindActionCreators(
    { loadDealFormsRequest, readFormsRequest },
    dispatch,
  ),
});

export default withRouter(
  connect(mapStateToProps, mapDispatchToProps)(FormsAndAttachedFiles),
);
